import React, { useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";

const TIME_PERIODS = [
  { label: "Today", value: "today" },
  { label: "This Week", value: "week" },
  { label: "This Month", value: "month" },
  { label: "This Year", value: "year" },
  { label: "All Time", value: "all" },
];

// Fixed color palette
const FIXED_COLORS = [
  "#6EC6FF", // Blue
  "#7CF6C7", // Green
  "#FFE082", // Yellow
  "#FF8A80", // Red
  "#80D6FF", // Cyan
];

const usePieChart = ({
  data,
  heading = "",
  defaultPeriod = "all",
  showDetails = true,
  onChange,
  loading = false,
  customHeight = "min-h-[320px]",
}) => {
  const [period, setPeriod] = useState(defaultPeriod);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Handle period change
  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    setIsDropdownOpen(false);
    if (onChange) {
      onChange(newPeriod);
    }
  };

  // Filter data by period
  const filteredData = useMemo(() => {
    if (period === "all") return data;
    const now = new Date();
    return data.filter((item) => {
      const itemDate = new Date(item.date);
      if (period === "today") {
        return itemDate.toDateString() === now.toDateString();
      } else if (period === "week") {
        const weekAgo = new Date(now);
        weekAgo.setDate(now.getDate() - 7);
        return itemDate >= weekAgo && itemDate <= now;
      } else if (period === "month") {
        return (
          itemDate.getMonth() === now.getMonth() &&
          itemDate.getFullYear() === now.getFullYear()
        );
      } else if (period === "year") {
        return itemDate.getFullYear() === now.getFullYear();
      }
      return true;
    });
  }, [data, period]);

  // Aggregate by label
  const categoryMap = useMemo(() => {
    const map = {};
    filteredData.forEach((item) => {
      if (!map[item.label]) map[item.label] = 0;
      map[item.label] += item.value;
    });
    return map;
  }, [filteredData]);

  const labels = Object.keys(categoryMap);
  const values = Object.values(categoryMap);
  const total = values.reduce((a, b) => a + b, 0);

  // ECharts data
  const pieData = labels.map((label, i) => ({
    value: categoryMap[label],
    name: label,
    itemStyle: { color: FIXED_COLORS[i % FIXED_COLORS.length] },
  }));

  const option = {
    backgroundColor: "#131313",
    tooltip: {
      show: window.innerWidth >= 768,
      trigger: "item",
      backgroundColor: "#fff",
      borderColor: "#eee",
      borderWidth: 1,
      textStyle: {
        color: "#222",
        fontWeight: "bold",
      },
      formatter: (params) => {
        const percent = total ? ((params.value / total) * 100).toFixed(1) : 0;
        return `
    <div style="margin-bottom:4px;">${heading}</div>
    <span style="display:inline-block;margin-right:8px;border-radius:10px;width:10px;height:10px;background:${
      params.color
    };"></span>
    <span>${params.name}</span>
    <span style="float:right;font-weight:bold;margin-left:12px;">
      ${params.value.toLocaleString("en-IN")} <span font-weight:normal;">(${percent}%)</span>
    </span>
  `;
      },
    },
    series: [
      {
        name: heading,
        type: "pie",
        radius: ["30%", "50%"],
        center: ["50%", "50%"],
        avoidLabelOverlap: true,
        itemStyle: {
          borderWidth: 0,
        },
        label: {
          show: window.innerWidth >= 768,
          color: "#fff",
          fontWeight: "bold",
          fontSize: 12,
          formatter: (params) => {
            const name = params.name;
            const words = name.split(" ");
            if (words.length > 2) {
              const mid = Math.ceil(words.length / 2);
              return (
                words.slice(0, mid).join(" ") +
                "\n" +
                words.slice(mid).join(" ")
              );
            }
            return name;
          },
          position: "outside",
          distanceToLabelLine: 5,
        },
        labelLine: {
          show: true,
          length: 15,
          length2: 10,
          smooth: false,
          lineStyle: {
            width: 1,
          },
        },
        emphasis: {
          scale: true,
          scaleSize: 10,
          itemStyle: {
            shadowBlur: 20,
            shadowOffsetX: 0,
            shadowColor: "rgba(0, 0, 0, 0.5)",
          },
        },
        data: pieData,
      },
    ],
  };

  // If no data
  if (labels.length === 0 && data.length === 0) {
    return (
      <div className={`bg-[#131313] rounded-lg p-5 text-white min-w-[280px] ${customHeight} shadow-md w-full flex flex-col`}>
        <div className="flex justify-between items-center mb-4">
          <div className="text-sm font-medium"> {heading} </div>
          <div className="relative">
            <button
              type="button"
              onClick={() => !loading && setIsDropdownOpen(!isDropdownOpen)}
              className="bg-zinc-800 text-yellow-400 border border-yellow-400 rounded-md px-2 py-1 font-medium flex justify-between items-center w-full md:w-auto min-w-[100px] text-xs md:text-sm"
              disabled={loading}
            >
              <span>
                {TIME_PERIODS.find((opt) => opt.value === period)?.label}
              </span>
              <svg
                className={`w-3 h-3 ml-2 transition-transform ${
                  isDropdownOpen ? "rotate-180" : ""
                }`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            {isDropdownOpen && (
              <div className="absolute right-0 z-10 mt-1 w-full md:w-auto min-w-[100px] bg-zinc-800 border border-yellow-400 rounded-md max-h-32 overflow-y-auto">
                {TIME_PERIODS.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => handlePeriodChange(option.value)}
                    className="px-2 py-1 text-yellow-400 hover:bg-zinc-700 cursor-pointer font-medium text-xs md:text-sm"
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-yellow-500 font-medium text-sm text-center">
            {loading
              ? "Loading..."
              : "Data is not available for the given time period."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-[#131313] rounded-lg p-5 text-white min-w-[280px] ${customHeight} shadow-md w-full`}
    >
      <div className="flex justify-between items-center">
        <div className="text-sm font-medium"> {heading} </div>
        <div className="relative">
          <button
            type="button"
            onClick={() => !loading && setIsDropdownOpen(!isDropdownOpen)}
            className="bg-zinc-800 text-yellow-400 border border-yellow-400 rounded-md px-2 py-1 font-medium flex justify-between items-center w-full md:w-auto min-w-[100px] text-xs md:text-sm"
            disabled={loading}
          >
            <span>
              {TIME_PERIODS.find((opt) => opt.value === period)?.label}
            </span>
            <svg
              className={`w-3 h-3 ml-2 transition-transform ${
                isDropdownOpen ? "rotate-180" : ""
              }`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
          {isDropdownOpen && (
            <div className="absolute right-0 z-10 mt-1 w-full md:w-auto min-w-[100px] bg-zinc-800 border border-yellow-400 rounded-md max-h-32 overflow-y-auto">
              {TIME_PERIODS.map((option) => (
                <div
                  key={option.value}
                  onClick={() => handlePeriodChange(option.value)}
                  className="px-2 py-1 text-yellow-400 hover:bg-zinc-700 cursor-pointer font-medium text-xs md:text-sm"
                >
                  {option.label}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="h-64 mx-auto my-2.5">
        <ReactECharts
          option={option}
          style={{ height: "250px", width: "100%" }}
          notMerge={true}
          lazyUpdate={true}
        />
      </div>
      {showDetails && (
        <div className="mt-2.5 max-h-20 overflow-y-auto scrollbar-hide">
          {[...new Set(data.map(d => d.label))].map((label, idx) => {
            const value = categoryMap[label] || 0;
            return (
              <div
                key={label}
                className="flex items-center mb-1.5 text-sm justify-between"
              >
                <div className="flex items-center">
                  <span
                    className="inline-block w-3 h-3 rounded-full mr-2"
                    style={{
                      backgroundColor: FIXED_COLORS[idx % FIXED_COLORS.length],
                    }}
                  ></span>
                  <span>
                    {label} (
                    {total ? ((value / total) * 100).toFixed(1) : 0}
                    %)
                  </span>
                </div>
                <span className="min-w-[40px] text-right">
                  {value}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default usePieChart;
